import React, { useEffect, useState } from "react";
import { adminApi } from "@/api/adminOneClient";

const STATUSES = ["OPTIMAL", "HEALTHY", "DEGRADED", "AT_RISK", "CRITICAL"];
const DIMENSIONS = [
  { key: "operational_readiness", label: "OPS" },
  { key: "demand_forecast",       label: "DEM" },
  { key: "capacity_forecast",     label: "CAP" },
  { key: "quality",               label: "QUA" },
  { key: "risk",                  label: "RSK" },
];
function statusColor(st) {
  return { OPTIMAL: "#7AC860", HEALTHY: "#7AC860", DEGRADED: "#F5C842", AT_RISK: "#E8A020", CRITICAL: "#C84A3A" }[st] || "#60B8E8";
}

export default function AdminServiceIntelligence() {
  const [services, setServices] = useState([]);
  const [filter, setFilter] = useState({ status: "", category: "" });
  const [active, setActive] = useState(null);
  const [healthcareEvents, setHealthcareEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.status)   params.status = filter.status;
      if (filter.category) params.category = filter.category;
      const { data } = await adminApi.services(params);
      setServices(data?.services || []);
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  const open = async (svc) => {
    setActive(svc);
    try {
      const { data } = await adminApi.healthcareMapForService(svc.key, 25);
      setHealthcareEvents(data?.events || []);
    } catch { setHealthcareEvents([]); }
  };

  const categories = Array.from(new Set(services.map((s) => s.category))).sort();

  return (
    <div className="admin-page mesh-shell" data-testid="admin-services">
      <div className="mesh-bg-radar" aria-hidden="true" />
      <div className="mesh-bg-particles" aria-hidden="true" />

      <div className="admin-page__head">
        <h2 className="admin-page__title">Service Intelligence</h2>
        <div className="mesh-eyebrow">26 CareLogix services · 5 score dimensions each</div>
      </div>

      <section className="admin-section mesh-section">
        <div className="admin-filters" style={{ marginBottom: 10 }}>
          <select value={filter.status} onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))} data-testid="svc-filter-status">
            <option value="">All Status</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filter.category} onChange={(e) => setFilter((f) => ({ ...f, category: e.target.value }))} data-testid="svc-filter-cat">
            <option value="">All Categories</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <button type="button" className="admin-linkbtn" onClick={load} data-testid="svc-refresh">Refresh</button>
        </div>

        {loading ? <div className="admin-saved">Loading…</div> : (
          <div className="mesh-service-grid">
            {services.map((s) => (
              <article
                key={s.id || s.key}
                className={`mesh-service-card ${active?.key === s.key ? "is-active" : ""}`}
                style={{ borderColor: statusColor(s.status) }}
                onClick={() => open(s)}
                data-testid={`svc-card-${s.key}`}
              >
                <header className="mesh-service-card__head">
                  <span className="mesh-pill" style={{ borderColor: statusColor(s.status), color: statusColor(s.status) }}>{s.status}</span>
                  <span className="mesh-pill mesh-pill--ghost">{s.category}</span>
                  <span className="mesh-score"><span className="mesh-score__num">{s.health_index}</span><span className="mesh-score__lbl">/100</span></span>
                </header>
                <h3 className="mesh-service-card__title">{s.name}</h3>
                <p className="mesh-service-card__sum">{s.summary}</p>
                <div className="mesh-subscores">
                  {DIMENSIONS.map((d) => {
                    const v = s.scores?.[d.key] ?? 0;
                    return (
                      <span key={d.key} className="mesh-subscore" title={`${d.key}: ${v}`}>
                        <span className="mesh-subscore__k">{d.label}</span>
                        <span className="mesh-subscore__bar"><span style={{ width: `${v}%` }} /></span>
                      </span>
                    );
                  })}
                </div>
                <div className="mesh-service-card__pulse" style={{ background: statusColor(s.status) }} aria-hidden="true" />
              </article>
            ))}
            {services.length === 0 && <div className="admin-foot-note">No services match filters.</div>}
          </div>
        )}
      </section>

      {active && (
        <section className="admin-section mesh-section" data-testid="svc-detail">
          <h3 className="admin-section__title">{active.name} · Healthcare events</h3>
          {healthcareEvents.length === 0 ? (
            <div className="admin-foot-note">No healthcare events directly mapped to {active.name}.</div>
          ) : (
            <ol className="mesh-timeline">
              {healthcareEvents.map((ev) => (
                <li key={ev.id} className="mesh-timeline__item" style={{ borderColor: statusColor(active.status) }}>
                  <span className="mesh-timeline__time">{ev.kind}</span>
                  {ev.asset && <span className="mesh-pill mesh-pill--gold">{ev.asset}</span>}
                  <span className="mesh-pill" style={{ borderColor: statusColor(active.status), color: statusColor(active.status) }}>{ev.severity}</span>
                  <span className="mesh-timeline__title">{ev.title}</span>
                </li>
              ))}
            </ol>
          )}
        </section>
      )}
    </div>
  );
}
